<template>
  <div
    v-click-outside="clickOutsideConfig"
    :data-testid="testid"
    class="drop-down-container"
    :class="{ visible: open }"
  >
    <button class="trigger" type="button" @click="toggle">
      <slot name="trigger"></slot>
    </button>

    <transition
      name="expand"
      @enter="enter"
      @after-enter="afterEnter"
      @leave="leave"
    >
      <div v-show="open" class="content" :data-testid="testid + '-content'">
        <slot name="content"></slot>
      </div>
    </transition>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import vClickOutside from '@/directives/clickOutside'

defineOptions({ name: 'DropDown' })

const props = withDefaults(
  defineProps<{
    trigger?: boolean
    expanded?: boolean
    height?: string
    clickOutside?: boolean
    testid?: string
  }>(),
  {
    trigger: true,
    height: 'auto',
    clickOutside: true,
    testid: '',
  },
)

const emit = defineEmits<{
  toggle: [open: boolean]
}>()

const open = ref(false)

onMounted(() => {
  open.value = !!props.expanded
})

watch(
  () => props.expanded,
  (value) => {
    open.value = !!value
  },
)

const clickOutsideConfig = computed(() => ({
  handler: tryClose,
  disabled: !props.clickOutside,
}))

function toggle(): void {
  if (props.trigger !== false) {
    open.value = !open.value
    emit('toggle', open.value)
  }
}

function tryClose(): void {
  if (props.clickOutside) {
    close()
  }
}

function close(): void {
  open.value = false
}

function enter(element: Element): void {
  const el = element as HTMLElement
  el.style.height = 'auto'
  const height = getComputedStyle(el).height
  el.style.height = '0'
  // eslint-disable-next-line
  window.getComputedStyle(el).height
  setTimeout(() => {
    el.style.height = props.height === 'auto' ? height : props.height
  })
}

function afterEnter(element: Element): void {
  const el = element as HTMLElement
  el.style.height = props.height
}

function leave(element: Element): void {
  const el = element as HTMLElement
  const height = getComputedStyle(el).height
  el.style.height = height
  // eslint-disable-next-line
  window.getComputedStyle(el).height
  setTimeout(() => {
    el.style.height = '0'
  })
}

// `open` is exposed (not just `close`) so parent components can read AND drive
// the expand state via a template ref — e.g. MapSidebarWidgets opens a dropdown
// when its widget gains content (`$refs[key].open = true`). Vue's expose proxy
// unwraps the ref, so the assignment writes through to `open.value`. This
// restores the Vue 2 `this.$refs[key].open` contract the consumers rely on.
defineExpose({ open, close })
</script>
<style scoped lang="scss" src="./drop-down.scss"></style>
